import { useMutation, useQuery } from '@apollo/client';
import { gql } from '__generated__/gql';
import {
  InvestmentEntityDocumentStatusEnumType,
  InvestmentEntityDocumentTypeEnumType,
} from '__generated__/graphql';
import { FC } from 'react';

import Badge from 'primitives/Badge';
import Button from 'primitives/Button';
import ConfirmButton from 'primitives/ConfirmButton';
import LoadingIndicator from 'primitives/LoadingIndicator';
import SlideOver from 'primitives/SlideOver';
import Toast from 'primitives/Toast';
import useToast from 'primitives/Toast/useToast';

import ErrorMessage from 'components/ErrorMessage';
import { FormInput, FormPanelWithReadMode } from 'components/FormPanel';

import documentSigningStatusToReadable from 'utils/enums/document-signing-status-to-readable';

import InvestmentEntityFileTypeOptions from '../constants';

const GET_INVESTMENT_ENTITY_DOCUMENT_QUERY = gql(`
  query InvestmentEntityDocument($id: ID!) {
    investmentEntityDocument(id: $id) {
      id
      investmentEntityId
      type
      name
      description
      status
      signingStatus
      url
    }
  }
`);

const RETRY_CONTRIBUTION_AGREEMENT_MUTATION = gql(`
  mutation RetryContributionAgreementFromDocument($investmentEntityId: ID!) {
    retryContributionAgreement(investmentEntityId: $investmentEntityId) {
      id
      status
      signingStatus
      url
    }
  }
`);

const InvestmentEntityDocumentSlideOver: FC<{
  open: boolean;
  documentId: string;
  onClose: () => void;
}> = ({ open = false, documentId, onClose }) => {
  const { loading, error, data, refetch } = useQuery(GET_INVESTMENT_ENTITY_DOCUMENT_QUERY, {
    variables: {
      id: documentId,
    },
    skip: !open,
  });
  const [retryContributionAgreement, { loading: retryLoading, error: retryError }] = useMutation(
    RETRY_CONTRIBUTION_AGREEMENT_MUTATION
  );
  const { showToast, handleToastOpen, handleToastClose } = useToast();

  function renderContent() {
    if (loading) return <LoadingIndicator />;

    if (error || !data) return <ErrorMessage error={error} refetch={refetch} />;

    const investmentEntityDocument = data.investmentEntityDocument;

    return (
      <>
        <FormPanelWithReadMode
          loading={loading}
          error={error}
          onSubmit={data => {}}
          title="Investment Entity Document"
        >
          <FormInput
            type="text"
            fieldName="name"
            label="Name"
            defaultValue={investmentEntityDocument.name}
          />
          <FormInput
            type="text"
            fieldName="description"
            label="Description"
            defaultValue={investmentEntityDocument.description}
          />
          <FormInput
            type="select"
            fieldName="type"
            label="Type"
            defaultValue={investmentEntityDocument.type}
            options={InvestmentEntityFileTypeOptions}
          />
        </FormPanelWithReadMode>
        {investmentEntityDocument.type ===
          InvestmentEntityDocumentTypeEnumType.ContributionAgreement && (
          <div className="mb-4">
            {(() => {
              const { label, color } = documentSigningStatusToReadable(
                investmentEntityDocument.signingStatus
              );
              return <Badge label={label} color={color} showIndicator />;
            })()}
          </div>
        )}
        {investmentEntityDocument.url && (
          <Button onClick={() => window.open(investmentEntityDocument.url!, '_blank')}>
            View Document
          </Button>
        )}
        {investmentEntityDocument.type ===
          InvestmentEntityDocumentTypeEnumType.ContributionAgreement &&
          investmentEntityDocument.status === InvestmentEntityDocumentStatusEnumType.Draft &&
          investmentEntityDocument.investmentEntityId && (
            <ConfirmButton
              onConfirm={async () => {
                await retryContributionAgreement({
                  variables: { investmentEntityId: investmentEntityDocument.investmentEntityId! },
                });
                refetch();
                handleToastOpen();
              }}
              loading={retryLoading}
              error={retryError}
              description="If this Contribution Agreement hasn't been sent yet, this will send it. If it's still pending signature, this will resend the signing reminder to the investor. If it's stuck (expired/rejected/errored), this will generate a fresh signing request."
            >
              Retry / Recheck Signing
            </ConfirmButton>
          )}
      </>
    );
  }

  return (
    <>
      <SlideOver
        open={open}
        onClose={onClose}
        title="Investment Entity Document"
        subTitle="Manage and audit deal document details"
      >
        {renderContent()}
      </SlideOver>
      <Toast
        show={showToast}
        handleClose={handleToastClose}
        title="Contribution Agreement Updated"
        message="The signing request was sent, resent, or its status was refreshed."
      />
    </>
  );
};

export default InvestmentEntityDocumentSlideOver;
